import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, InputLabel, ListSubheader, MenuItem, Select, Typography,
} from '@mui/material';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';

export interface FlatLimitInfo {
  error: string;
  flat_limit: number;
  flat_count: number;
  new_units: { normalized_address: string; flat: string }[];
}

export interface FlatUnit { normalized_address: string; flat: string }

// Shown when saving a certificate would add a flat beyond what the user has bought.
// onResolve({}) = an extra flat was bought, so retry as is; onResolve({ unit }) = retry under that existing flat.
const FlatLimitDialog: React.FC<{
  info: FlatLimitInfo | null;
  token: string;
  onResolve: (result: { unit?: FlatUnit } | null) => void;
}> = ({ info, token, onResolve }) => {
  const { refreshSubscriptionStatus } = useAuth();
  const [properties, setProperties] = useState<{ normalized_address: string; flats: string[] }[]>([]);
  const [selected, setSelected] = useState('');
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!info) return;
    setSelected('');
    setError(null);
    axios.get(`${config.apiUrl}/api/flats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProperties(res.data.properties || []))
      .catch(() => setProperties([]));
  }, [info, token]);

  const buyExtra = async () => {
    setBuying(true);
    setError(null);
    try {
      await axios.post(`${config.apiUrl}/api/flats/buy-extra`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await refreshSubscriptionStatus();
      onResolve({});
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not buy an extra flat');
    } finally {
      setBuying(false);
    }
  };

  const useSelected = () => {
    const [address, flat] = selected.split('||');
    onResolve({ unit: { normalized_address: address, flat } });
  };

  return (
    <Dialog open={Boolean(info)} onClose={() => onResolve(null)} maxWidth="sm" fullWidth>
      <DialogTitle>This goes over your flats</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{info?.error}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>1. It is a new flat</Typography>
        <Button variant="contained" onClick={buyExtra} disabled={buying} sx={{ textTransform: 'none', mb: 2 }}>
          {buying ? <CircularProgress size={18} /> : 'Buy an additional flat'}
        </Button>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2 }}>2. The address is a mistake</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          If the AI misread the address, put this certificate under a flat you already have.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Existing flat</InputLabel>
            <Select value={selected} label="Existing flat" onChange={e => setSelected(e.target.value)}>
              {properties.flatMap(p => [
                <ListSubheader key={`h-${p.normalized_address}`}>{p.normalized_address}</ListSubheader>,
                ...p.flats.map(f => (
                  <MenuItem key={`${p.normalized_address}||${f}`} value={`${p.normalized_address}||${f}`}>{f}</MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={useSelected} disabled={!selected} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
            Use this flat
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onResolve(null)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlatLimitDialog;
